import _ from 'lodash';
import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';

import {useBuildCancelToken} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {CancelError} from '../../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {LinkedConversationStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {ResolveStateProps} from '../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
} from '../../../../../../libs/shared/ui/src/components/loaders/asyncDataHelpers';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch} from '../../../../../helpers/appReduxHelpers';
import {
  ActivityModel,
  DetailedActivityConversationLinkedModel,
  LinkedConversationActivityRolesEnum,
} from '../../../../../models/api/activityModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {AppState} from '../../../../../store/appState';
import {fetchOtherLinkedConversationsAsyncAction} from '../../../../../store/data/conversations/linkedConversations/linkedConversationsActions';
import {makeGetOriginalConversationFromActivities} from '../../../../../store/data/conversations/linkedConversations/linkedConversationsSelectors';
import {getActivities} from '../../../../../store/data/dataSelectors';
import {ConversationSummaryLinkedConversations} from './conversationSummaryLinkedConversations';
import {ConversationSummarySectionsEnum} from './conversationSummarySections';
import {
  withUnpinnedActivityTracking,
  WithUnpinnedActivityTrackingInjectedProps,
} from './withUnpinnedActivityTracking';

/*
 * Props + Types.
 */

export interface ConversationSummaryLinkedConversationsContainerPublicProps {
  panelId: ViewerPanelId;
  linkedConversationActivities: AsyncData<ReadonlyArray<DetailedActivityConversationLinkedModel>>;
  onLinkedConversationsRetryClick: () => void;
  onRequestClose: () => void;
  linkStatus: LinkedConversationStatusesEnum;
  isCompact?: boolean;
  sectionType?: ConversationSummarySectionsEnum;
}

type ConversationSummaryLinkedConversationsContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type ConversationSummaryLinkedConversationsContainerDispatchProps = ResolveDispatchProps<
  typeof dispatchProps
>;

type ConversationSummaryLinkedConversationsContainerProps =
  ConversationSummaryLinkedConversationsContainerPublicProps &
    ConversationSummaryLinkedConversationsContainerStateProps &
    ConversationSummaryLinkedConversationsContainerDispatchProps &
    WithUnpinnedActivityTrackingInjectedProps;

/*
 * Component.
 */

const ConversationSummaryLinkedConversationsContainerComponent: React.FC<
  ConversationSummaryLinkedConversationsContainerProps
> = (props) => {
  const {
    panelId,
    linkedConversationActivities,
    recentlyUnpinnedActivities,
    linkStatus,
    originalConversationActivity,
  } = props;
  const dispatch = useAppDispatch();
  const {buildCancelToken} = useBuildCancelToken();

  const linkedOrRecentlyUnlinkedConversations =
    linkedConversationActivities.status === AsyncStatusesEnum.LOADED
      ? asyncDataLoaded(
          collectLinkedOrRecentlyUnlinkedActivities(
            linkedConversationActivities.data,
            recentlyUnpinnedActivities,
          ),
        )
      : linkedConversationActivities;
  const [otherLinkedConversationsAsyncData, setOtherLinkedConversationsAsyncData] =
    useState<AsyncData<ReadonlyArray<ConversationModel>>>(asyncDataLoading());

  const fetchOtherLinkedConversations = useCallback(async () => {
    try {
      if (!originalConversationActivity) {
        throw new Error('Invalid original conversation');
      }
      const otherLinkedConversations = await dispatch(
        fetchOtherLinkedConversationsAsyncAction(
          originalConversationActivity.conversationId,
          buildCancelToken(),
        ),
      );
      setOtherLinkedConversationsAsyncData(asyncDataLoaded(otherLinkedConversations));
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }
      setOtherLinkedConversationsAsyncData(asyncDataFailed(error));
    }
  }, [buildCancelToken, dispatch, originalConversationActivity]);

  useEffect(() => {
    if (linkedConversationActivities.status === AsyncStatusesEnum.LOADED) {
      fetchOtherLinkedConversations();
    }
  }, [
    fetchOtherLinkedConversations,
    linkStatus,
    linkedConversationActivities.status,
    originalConversationActivity,
  ]);

  if (linkStatus === LinkedConversationStatusesEnum.LINKED) {
    return (
      <ConversationSummaryLinkedConversations
        linkedOrRecentlyUnlinkedConversations={linkedOrRecentlyUnlinkedConversations}
        onLinkedConversationsRetryClick={props.onLinkedConversationsRetryClick}
        panelId={panelId}
        linkStatus={linkStatus}
        otherLinkedConversations={otherLinkedConversationsAsyncData}
        onOtherLinkedConversationsRetryClick={fetchOtherLinkedConversations}
        onRequestClose={props.onRequestClose}
        isCompact={Boolean(props.isCompact)}
        sectionType={props.sectionType}
      />
    );
  }
  return (
    <ConversationSummaryLinkedConversations
      linkedOrRecentlyUnlinkedConversations={linkedOrRecentlyUnlinkedConversations}
      onLinkedConversationsRetryClick={props.onLinkedConversationsRetryClick}
      panelId={panelId}
      linkStatus={linkStatus}
      onRequestClose={props.onRequestClose}
      isCompact={Boolean(props.isCompact)}
      sectionType={props.sectionType}
    />
  );
};

function collectLinkedOrRecentlyUnlinkedActivities(
  pinnedActivities: ReadonlyArray<ActivityModel>,
  recentlyUnpinnedActivities: ReadonlyArray<ActivityModel>,
): ReadonlyArray<ActivityModel> {
  return _([...pinnedActivities, ...recentlyUnpinnedActivities])
    .uniqBy('id')
    .sortBy('date')
    .value();
}

/*
 * Selectors.
 */

function getRecentlyUnpinnedActivityIds(
  _state: AppState,
  props: ConversationSummaryPinnedActivitiesContainerConnectProps,
) {
  return props.unpinnedActivityIds;
}

function getLinkedConversationActivities(
  _state: AppState,
  props: ConversationSummaryPinnedActivitiesContainerConnectProps,
) {
  return props.linkedConversationActivities.status === AsyncStatusesEnum.LOADED
    ? props.linkedConversationActivities.data
    : [];
}

function makeGetRecentlyUnpinnedActivities() {
  return createSelector(
    [getActivities, getRecentlyUnpinnedActivityIds],
    (activities, unpinnedActivityIds) =>
      _(activities).pick(unpinnedActivityIds).values().value() as ReadonlyArray<ActivityModel>,
  );
}

function makeGetOriginalConversationActivity() {
  return createSelector([getLinkedConversationActivities], (linkedConversationActivities) =>
    linkedConversationActivities.find(
      (conversationActivity) =>
        conversationActivity.linkedConversationRole === LinkedConversationActivityRolesEnum.ORIGINAL &&
        conversationActivity.linkedConversationId,
    ),
  );
}

/*
 * Redux.
 */

type ConversationSummaryPinnedActivitiesContainerConnectProps =
  ConversationSummaryLinkedConversationsContainerPublicProps & WithUnpinnedActivityTrackingInjectedProps;

const mapStateToProps = makeMapStateToProps<ConversationSummaryPinnedActivitiesContainerConnectProps>()(
  () => ({
    recentlyUnpinnedActivities: makeGetRecentlyUnpinnedActivities(),
    originalConversation: makeGetOriginalConversationFromActivities(getLinkedConversationActivities),
    originalConversationActivity: makeGetOriginalConversationActivity(),
  }),
);

const dispatchProps = {};

export const ConversationSummaryLinkedConversationsContainer = compose(
  withUnpinnedActivityTracking,
  connect(mapStateToProps, dispatchProps),
)(
  ConversationSummaryLinkedConversationsContainerComponent,
) as React.ComponentType<ConversationSummaryLinkedConversationsContainerPublicProps>;
